import React, {
  useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Empty,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import * as api from 'util/api';

const { Text } = Typography;

const STORE_LABELS = {
  GPNZ: 'NZ',
  GPAU: 'AU',
};

function shopifyProductAdminHref(store, shopifyProductId) {
  if (shopifyProductId == null || shopifyProductId === '') return null;
  const slug = store === 'GPAU' ? 'garageproject-australia' : 'garage-project';
  return `https://${slug}.myshopify.com/admin/products/${shopifyProductId}`;
}

function matchTag(link) {
  if (!link?.shopifyProductId) {
    return <Tag>none</Tag>;
  }
  if (link.ambiguous) return <Tag color="orange">ambiguous</Tag>;
  if (link.matchMethod === 'manual') return <Tag color="blue">manual</Tag>;
  if (link.matchMethod === 'auto_name') return <Tag color="green">auto</Tag>;
  return <Tag color="green">linked</Tag>;
}

function candidateLabel(p) {
  const created = p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '';
  const variants = (p.variants || []).map((v) => v.variantTitle).filter(Boolean).join(', ');
  return [p.productTitle, p.productStatus, created].filter(Boolean).join(' — ')
    + (variants ? ` (${variants})` : '');
}

function normTitle(title) {
  return String(title ?? '').trim().toLowerCase();
}

function variantOptionLabel(variant) {
  const price = variant.price != null && variant.price !== ''
    ? `$${variant.price}`
    : null;
  return [variant.variantTitle, variant.variantSku, price].filter(Boolean).join(' — ');
}

/** Existing link on this product, else exact title, else the only variant. */
function defaultVariantMap(product, skus) {
  const variants = product?.variants || [];
  const map = {};
  skus.forEach((sku) => {
    const current = variants.find((variant) => (
      sku.currentVariantId != null
      && String(variant.shopifyVariantId) === String(sku.currentVariantId)
    ));
    if (current) {
      map[sku.skuKey] = String(current.shopifyVariantId);
      return;
    }
    const want = normTitle(sku.expectedTitle);
    const exact = want
      ? variants.find((variant) => normTitle(variant.variantTitle) === want)
      : null;
    if (exact) {
      map[sku.skuKey] = String(exact.shopifyVariantId);
      return;
    }
    if (variants.length === 1) {
      map[sku.skuKey] = String(variants[0].shopifyVariantId);
    }
  });
  return map;
}

function ShopifyProductLinker({
  skuKey,
  productMatrixId,
  store,
  disabled,
  onSaved,
}) {
  const storeLabel = STORE_LABELS[store] || store;
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchProducts, setSearchProducts] = useState([]);
  const [searchValue, setSearchValue] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [variantBySku, setVariantBySku] = useState({});
  const searchTimerRef = useRef(null);
  const canLoad = productMatrixId != null && productMatrixId !== '';

  const candidatesQuery = useQuery({
    queryKey: ['shopify_sku_link_candidates', store, productMatrixId],
    queryFn: () => api.getShopifySkuLinkCandidates({
      store,
      productMatrixId,
    }),
    enabled: canLoad,
    onError: (err) => console.error('Error loading Shopify links:', err),
  });

  const candidates = candidatesQuery.data?.candidates || [];
  const links = candidatesQuery.data?.links || [];
  const skus = candidatesQuery.data?.skus || [];
  const loading = candidatesQuery.isFetching && candidatesQuery.data == null;

  const skuLink = useMemo(
    () => links.find((row) => Number(row.skuKey) === Number(skuKey)) || null,
    [links, skuKey],
  );

  const currentProduct = useMemo(() => {
    if (!skuLink?.shopifyProductId) return null;
    return candidates.find((p) => (
      String(p.shopifyProductId) === String(skuLink.shopifyProductId)
    )) || null;
  }, [candidates, skuLink]);

  const currentVariant = useMemo(() => {
    if (!currentProduct || !skuLink?.shopifyVariantId) return null;
    return (currentProduct.variants || []).find((v) => (
      String(v.shopifyVariantId) === String(skuLink.shopifyVariantId)
    )) || null;
  }, [currentProduct, skuLink]);

  const searchOptions = useMemo(
    () => searchProducts.map((p) => ({
      value: String(p.shopifyProductId),
      label: candidateLabel(p),
    })),
    [searchProducts],
  );

  const variantOptions = useMemo(
    () => (selectedProduct?.variants || []).map((variant) => ({
      value: String(variant.shopifyVariantId),
      label: variantOptionLabel(variant),
    })),
    [selectedProduct],
  );

  const href = shopifyProductAdminHref(store, skuLink?.shopifyProductId);
  const title = currentProduct?.productTitle || skuLink?.shopifyProductId || null;

  const closeModal = () => {
    setModalOpen(false);
    setSelectedProduct(null);
    setSearchProducts([]);
    setSearchValue(null);
    setVariantBySku({});
  };

  const beginMapping = (product) => {
    if (!product) return;
    setSelectedProduct(product);
    setVariantBySku(defaultVariantMap(product, skus));
  };

  const linkBeer = async (shopifyProductId) => {
    if (productMatrixId == null) return;
    setSaving(true);
    try {
      const result = await api.setShopifyBeerLink({
        productMatrixId,
        store,
        shopifyProductId,
      });
      if (result?.failed > 0) {
        message.warning(
          `Linked ${result.linked}/${result.linked + result.failed} SKU(s)`,
        );
      } else if (shopifyProductId == null) {
        message.success('Shopify link cleared for this beer');
        setSelectedProduct(null);
      } else {
        message.success(`Shopify link saved for ${result?.linked || 0} SKU(s)`);
      }
      await queryClient.invalidateQueries({
        queryKey: ['shopify_sku_link_candidates', store, productMatrixId],
      });
      onSaved?.();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Failed to save Shopify link');
    } finally {
      setSaving(false);
    }
  };

  const saveMappings = async () => {
    if (!selectedProduct || productMatrixId == null) return;
    const rows = skus.filter((sku) => variantBySku[sku.skuKey]);
    if (rows.length === 0) {
      message.warning('Pick a variant for at least one SKU');
      return;
    }
    setSaving(true);
    try {
      const outcomes = await Promise.all(rows.map(async (sku) => {
        try {
          await api.setShopifySkuLink({
            skuKey: sku.skuKey,
            store,
            shopifyProductId: selectedProduct.shopifyProductId,
            shopifyVariantId: variantBySku[sku.skuKey],
          });
          return { ok: true };
        } catch (err) {
          return {
            ok: false,
            error: err?.response?.data?.error || 'Failed to save Shopify link',
          };
        }
      }));
      const linked = outcomes.filter((row) => row.ok).length;
      const failed = outcomes.length - linked;
      if (failed > 0) {
        const first = outcomes.find((row) => !row.ok);
        message.warning(
          `Linked ${linked}/${outcomes.length} SKU(s). ${first?.error || ''}`,
        );
      } else {
        message.success(`Shopify link saved for ${linked} SKU(s)`);
        closeModal();
      }
      await queryClient.invalidateQueries({
        queryKey: ['shopify_sku_link_candidates', store, productMatrixId],
      });
      onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = (q) => {
    clearTimeout(searchTimerRef.current);
    if (!q || String(q).trim().length < 2) {
      setSearchProducts([]);
      setSearchValue(null);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await api.searchShopifySkuLinkProducts({ store, q });
        setSearchProducts(data?.products || []);
      } catch (err) {
        console.error('Shopify product search failed:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  if (productMatrixId == null || productMatrixId === '') {
    return <Text type="secondary">Save the product before linking Shopify.</Text>;
  }

  return (
    <div style={{ width: '100%', minWidth: 0 }}>
      {loading ? (
        <div style={{ marginBottom: 8 }}>
          <Spin size="small" />
        </div>
      ) : null}
      {!loading && skuLink?.shopifyProductId ? (
        <div style={{ marginBottom: 8 }}>
          <Space wrap size={4}>
            {href ? (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {title}
              </a>
            ) : (
              <Text>{title}</Text>
            )}
            {matchTag(skuLink)}
          </Space>
          {currentVariant?.variantTitle ? (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {currentVariant.variantTitle}
              </Text>
            </div>
          ) : null}
        </div>
      ) : null}
      {!loading && !skuLink?.shopifyProductId ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            candidates.length > 0
              ? `${candidates.length} candidate match${candidates.length === 1 ? '' : 'es'}`
              : 'No Shopify product linked'
          }
          style={{ margin: '8px 0' }}
        />
      ) : null}

      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined />}
        onClick={() => setModalOpen(true)}
        disabled={disabled}
      >
        {skuLink?.shopifyProductId
          ? `Manage Shopify ${storeLabel} link`
          : `Link Shopify ${storeLabel} product`}
      </Button>

      <Modal
        title={`Shopify ${storeLabel} link`}
        visible={modalOpen}
        onCancel={closeModal}
        footer={selectedProduct ? (
          <Space>
            <Button
              onClick={() => {
                setSelectedProduct(null);
                setSearchValue(null);
              }}
              disabled={saving}
            >
              Back
            </Button>
            <Button
              type="primary"
              loading={saving}
              disabled={disabled || skus.length === 0}
              onClick={saveMappings}
            >
              Save links
            </Button>
          </Space>
        ) : (
          <Button type="primary" onClick={closeModal}>
            Done
          </Button>
        )}
        width={640}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Pick a Shopify product, then map each SKU to a variant.
        </Text>

        {selectedProduct ? (
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ fontSize: 13 }}>
              {`Map SKUs — ${selectedProduct.productTitle || selectedProduct.shopifyProductId}`}
            </Text>
            {skus.length === 0 ? (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">No Shopify-sold SKUs on this beer.</Text>
              </div>
            ) : null}
            {skus.map((sku) => (
              <div
                key={sku.skuKey}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div>{sku.skuName || `SKU ${sku.skuKey}`}</div>
                  {sku.expectedTitle ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {sku.expectedTitle}
                    </Text>
                  ) : null}
                </div>
                <Select
                  allowClear
                  placeholder="Pick variant"
                  style={{ width: 260, flexShrink: 0 }}
                  options={variantOptions}
                  value={variantBySku[sku.skuKey] ?? null}
                  disabled={disabled || saving}
                  onChange={(val) => setVariantBySku((prev) => ({
                    ...prev,
                    [sku.skuKey]: val ?? null,
                  }))}
                />
              </div>
            ))}
          </div>
        ) : null}

        {candidates.length > 0 ? (
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ fontSize: 13 }}>Candidates</Text>
            {candidates.map((p) => (
              <div
                key={p.shopifyProductId}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '8px 0',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <Text>{candidateLabel(p)}</Text>
                <Button
                  size="small"
                  type="primary"
                  loading={saving}
                  disabled={disabled}
                  onClick={() => beginMapping(p)}
                >
                  Link
                </Button>
              </div>
            ))}
          </div>
        ) : null}

        <Text strong style={{ fontSize: 13 }}>Search cache</Text>
        <Select
          showSearch
          filterOption={false}
          placeholder="Search Shopify product title…"
          style={{ width: '100%', marginTop: 8, marginBottom: 16 }}
          options={searchOptions}
          value={searchValue}
          loading={searching}
          disabled={disabled || saving}
          onSearch={handleSearch}
          onChange={(val) => {
            setSearchValue(val ?? null);
            const product = searchProducts.find((p) => (
              String(p.shopifyProductId) === String(val)
            ));
            if (product) beginMapping(product);
          }}
          notFoundContent={searching ? 'Searching…' : 'Type to search cache'}
        />

        {skuLink?.shopifyProductId ? (
          <Button
            danger
            disabled={disabled}
            loading={saving}
            onClick={() => linkBeer(null)}
          >
            Clear link
          </Button>
        ) : null}
      </Modal>
    </div>
  );
}

ShopifyProductLinker.propTypes = {
  skuKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  productMatrixId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  store: PropTypes.oneOf(['GPNZ', 'GPAU']).isRequired,
  disabled: PropTypes.bool,
  onSaved: PropTypes.func,
};

ShopifyProductLinker.defaultProps = {
  productMatrixId: null,
  disabled: false,
  onSaved: undefined,
};

export default ShopifyProductLinker;
